import { ENTITY_TYPES } from '@sales-center/common'
import {
  addEditEntityLocation,
  getEmailAddressesAsString,
} from '@user/store/entityLocations'
import { useAppSelector } from '@user/store/hooks'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { getLeadDetail, getLeadValue } from '@user/store/lead'
import {
  getOpportunityDetail,
  getOpportunityValue,
} from '@user/store/opportunity'
import { isAPIValidationError } from '@user/store/services/crud'
import { FormikHelpers } from 'formik'
import { useCallback } from 'react'
import { toast } from '@sales-center/components'
import {
  AddServiceLocationFormModel,
  formModelToLocationInput,
} from '../formModel'
import { selectLeadDetail } from '@user/store/lead/slice'
import { selectOpportunityDetail } from '@user/store/opportunity/slice'
import {
  selectAddOpportunitySlice,
  upsertAddOppBillTo,
  upsertAddOppLocation,
} from '@user/store/opportunity/addOpportunity/addOpportunitySlice'
import { useSelector } from 'react-redux'
import { externalAppTimeoutError } from '@user/store/utilities/functions'
import {
  applyInvalidDomainErrorsToEmailCollection,
  EMAIL_VALIDATION_PREFIX,
  extractInvalidEmailsFromValidationMessages,
  parseEmailValidationErrorMessages,
} from '@user/store/emailValidation/functions'

type Params = {
  entityId: number
  entityType: ENTITY_TYPES
  onClose: () => void
  externalLocationId?: string | null
  externalBillToId?: string | null
}

export function useOnSubmit(params: Params, showUpdateCheckbox: boolean) {
  const {
    entityId,
    entityType,
    onClose,
    externalLocationId,
    externalBillToId,
  } = params
  const { billTo } = useSelector(selectAddOpportunitySlice)
  const { externalLinkId } = billTo
  const billToId = externalBillToId ?? externalLinkId
  const dispatch = useAppDispatch()

  const detail = useAppSelector((state) =>
    entityType === ENTITY_TYPES.Lead
      ? selectLeadDetail(state)
      : selectOpportunityDetail(state),
  )

  return useCallback(
    async (
      values: AddServiceLocationFormModel,
      helpers: FormikHelpers<AddServiceLocationFormModel>,
    ) => {
      const data = formModelToLocationInput(
        values,
        entityId,
        entityType,
        externalLocationId,
      )
      data.billToId = billToId?.toString()
      data.updateBillTo = showUpdateCheckbox ? data.updateBillTo : false

      const successMessage = `${
        data.billingLocation ? 'Bill-to' : 'Service Location'
      } successfully ${data.id ? 'updated.' : 'added.'}`

      try {
        const result = await dispatch(addEditEntityLocation(data))
        if (addEditEntityLocation.fulfilled.match(result)) {
          if (entityType == ENTITY_TYPES.Lead) {
            await dispatch(getLeadValue(entityId))
            if (detail?.id == entityId) dispatch(getLeadDetail(entityId))
          } else {
            await dispatch(getOpportunityValue(entityId))
            if (detail?.id == entityId) dispatch(getOpportunityDetail(entityId))
          }

          const updateInput = {
            ...result.payload.contact,
            altPhone: result.payload.contact?.altPhoneNumber ?? null,
            email:
              getEmailAddressesAsString(
                result.payload.contact?.emailAddresses,
              ) ?? null,
            phone: result.payload.contact?.phoneNumber ?? null,
            altPhoneExt: result.payload.contact?.altPhoneExt ?? null,
            company: result.payload.contact?.company ?? null,
            firstName: result.payload.contact?.firstName ?? null,
            id:
              result.payload.id > 0
                ? result.payload.id
                : result.payload.contact
                  ? +result.payload.contact.externalLinkId
                  : -1,
            lastName: result.payload.contact?.lastName ?? null,
            phoneExt: result.payload.contact?.phoneExt ?? null,
            isBillingLocation: data.billingLocation,
            updateBillingLocation: data.updateBillTo,
          }

          if (updateInput.isBillingLocation) {
            dispatch(upsertAddOppBillTo(updateInput))
          } else {
            dispatch(upsertAddOppLocation(updateInput))
          }

          toast.success(successMessage)

          const { draftAgreementsUpdated } = result.payload
          if (data.id && draftAgreementsUpdated) {
            toast.success(
              'Location information has been updated on any draft agreements.',
            )
          }

          onClose()
        } else {
          const { payload } = result
          if (payload) {
            if (isAPIValidationError(payload)) {
              const { title, errors } = payload

              if (errors) {
                helpers.setErrors({
                  locationTypeId: errors['LocationTypeId']?.join(', '),
                  divisionId: errors['DivisionId']?.join(', '),
                  firstName: errors['Contact.FirstName']?.join(', '),
                  lastName: errors['Contact.LastName']?.join(', '),
                  company: errors['Contact.Company']?.join(', '),
                  emailAddresses: errors['Contact.EmailAddresses'],
                  phoneNumber: errors['Contact.PhoneNumber']?.join(', '),
                  phoneExt: errors['Contact.PhoneExt']?.join(', '),
                })
              }

              toast.error(title)
            } else {
              if (externalAppTimeoutError(payload)) {
                return
              }

              const { Message } = payload

              if (Message?.includes(EMAIL_VALIDATION_PREFIX)) {
                const splitMessages = parseEmailValidationErrorMessages(Message)

                if (splitMessages.length) {
                  const invalidEmails =
                    extractInvalidEmailsFromValidationMessages(splitMessages)
                  const emailErrors = applyInvalidDomainErrorsToEmailCollection(
                    values.emailAddresses,
                    invalidEmails,
                  )

                  if (emailErrors) {
                    helpers.setErrors({ emailAddresses: emailErrors })
                  }

                  return
                }
              }

              toast.error(Message)
            }
          } else {
            toast.error('Unexpected error occurred.')
          }
        }
      } catch {
        toast.error('Unexpected error occurred.')
      }
    },
    [
      entityId,
      entityType,
      externalLocationId,
      billToId,
      dispatch,
      onClose,
      detail?.id,
      showUpdateCheckbox,
    ],
  )
}
